import { IconButton, IconButtonProps } from "@mgdx/ui/components/IconButton";
import { toastError } from "@mgdx/ui/components/Toast";
import { type InputHTMLAttributes, useCallback } from "react";
import { DropEvent, DropzoneOptions, useDropzone } from "react-dropzone";

export type FileObject = {
  file: File;
  data?: string;
};

export type Props = Omit<DropzoneOptions, "onDropAccepted" | "onDropRejected" | "onDrop" | "multiple" | "onError"> &
  Pick<InputHTMLAttributes<HTMLInputElement>, "title" | "capture"> & {
    onChangeFile?: (file: File) => void;
    icon: IconButtonProps["children"];
    /** ボタンの名前。写真を撮る・添付するなど、並ぶボタンと区別できる名前にする */
    "aria-label": string;
  };

export const InputFile = ({
  /**
   * @see https://react-dropzone.js.org/#src
   * DropzoneOptions
   */
  accept,
  maxSize,
  minSize,
  noClick,
  noDrag,
  noDragEventsBubbling,
  noKeyboard,
  onDragEnter,
  onDragLeave,
  onDragOver,
  preventDropOnDocument,
  /**
   * my props
   */
  onChangeFile,
  icon,
  title,
  "aria-label": ariaLabel,
  ...props
}: Props) => {
  const onDropAccepted = useCallback<NonNullable<DropzoneOptions["onDropAccepted"]>>(
    async (acceptedFiles: File[], event: DropEvent) => {
      if (onChangeFile) {
        onChangeFile(acceptedFiles[0]);
      }
    },
    [onChangeFile]
  );

  const onDropRejected = useCallback<NonNullable<DropzoneOptions["onDropRejected"]>>(async (fileRejections, event) => {
    toastError("このファイルは選択できません。");
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    accept,
    maxSize,
    minSize,
    multiple: false,
    noClick: true,
    noDrag,
    noDragEventsBubbling,
    noKeyboard,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDropAccepted,
    onDropRejected,
    preventDropOnDocument,
  });

  return (
    <div {...getRootProps({ style: { outline: "none" } })}>
      <IconButton variant="subtle" color="secondary" size="s" onClick={open} aria-label={ariaLabel} title={title}>
        {icon}
      </IconButton>
      <input
        {...getInputProps()}
        {...props}
        aria-label={ariaLabel}
        aria-hidden="true"
        tabIndex={-1}
        className="sr-only"
      />
    </div>
  );
};
